import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ApiError, api } from '@/lib/api/client';
import { LoginScreen } from './LoginScreen';

interface Session {
  email: string;
}

/**
 * Barrière d'authentification posée AUTOUR du shell, et non comme une route.
 *
 * Une route `/login` aurait demandé un layout parent et des redirections : le
 * routage par fichiers de TanStack a déjà coûté un bug de ce genre en
 * production (un parent sans <Outlet /> masquait ses enfants). Ici la question
 * est binaire — session valide ou pas — et n'a pas besoin d'URL.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isPending, error } = useQuery<Session>({
    queryKey: ['session'],
    queryFn: () => api<Session>('/auth/me'),
    // Un 401 est une réponse normale ici, pas une panne : ne pas retenter.
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-secondary">
        Chargement…
      </div>
    );
  }

  const nonAuthentifie = error instanceof ApiError && error.status === 401;
  if (nonAuthentifie || !data) {
    return (
      <LoginScreen onSuccess={() => queryClient.invalidateQueries({ queryKey: ['session'] })} />
    );
  }

  return <>{children}</>;
}

export function useSessionEmail(): string | undefined {
  return useQueryClient().getQueryData<Session>(['session'])?.email;
}
